import { HStack, IconButton, Text } from '@chakra-ui/react';
import { Minus, Plus } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** The highest value the + button reaches; unbounded when absent. */
  max?: number;
  /** How the value reads: `qty` → "3 ×", `days` → "14 days", `months` → "6 mo". */
  format: (value: number) => string;
  /** Per-recipient (blue dashed) vs neutral chrome. */
  variableStyle?: boolean;
  hasError?: boolean;
  valueMinW?: string;
  'aria-label'?: string;
}

/**
 * Numeric stepper with -/+ buttons. Two visual styles: the per-recipient
 * variant (blue dashed border, blue value) marks default values a proposal can
 * override; the neutral variant is for shared fields (usage-rights duration).
 */
export const QuantityStepper = ({
  value,
  onChange,
  min = 1,
  max,
  format,
  variableStyle = false,
  hasError = false,
  valueMinW = '34px',
  'aria-label': ariaLabel,
}: QuantityStepperProps) => {
  const { t } = useTranslation();
  const chrome = variableStyle
    ? {
        borderStyle: 'dashed' as const,
        borderColor: 'perRecipient.border',
        bg: 'perRecipient.surface',
      }
    : { borderStyle: 'solid' as const, borderColor: 'border.emphasized', bg: 'background' };

  const valueColor = variableStyle ? 'perRecipient.text' : 'dark';
  const glyphColor = variableStyle ? 'perRecipient.textMuted' : 'primary.500';

  return (
    <HStack
      gap="0"
      borderWidth="1px"
      rounded="lg"
      overflow="hidden"
      w="fit-content"
      // Beside a wrapping caption the row would squeeze it, and the overflow would hide the + button.
      flexShrink={0}
      role="group"
      aria-label={ariaLabel}
      {...chrome}
      {...(hasError && { borderColor: 'fg.error', borderStyle: 'solid' })}
    >
      <IconButton
        aria-label={t('common.decrease')}
        variant="ghost"
        size="xs"
        color={glyphColor}
        rounded="none"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        _hover={{ bg: variableStyle ? 'perRecipient.surfaceSubtle' : 'softCream' }}
      >
        <Minus width={14} height={14} />
      </IconButton>
      <Text
        minW={valueMinW}
        textAlign="center"
        fontFamily="heading"
        fontSize="13px"
        fontWeight="semibold"
        color={valueColor}
        px="1"
      >
        {format(value)}
      </Text>
      <IconButton
        aria-label={t('common.increase')}
        variant="ghost"
        size="xs"
        color={glyphColor}
        rounded="none"
        disabled={max !== undefined && value >= max}
        onClick={() => onChange(max === undefined ? value + 1 : Math.min(max, value + 1))}
        _hover={{ bg: variableStyle ? 'perRecipient.surfaceSubtle' : 'softCream' }}
      >
        <Plus width={14} height={14} />
      </IconButton>
    </HStack>
  );
};
